/**
 * Currency Utility Functions
 * Pure functions for currency management
 */
/**
 * Format money amount from minor units (cents) to a formatted string
 *
 * @param amountMinorUnits - Amount in minor units (e.g., 12345 for $123.45)
 * @param currencyCode - ISO 4217 currency code (e.g., "USD", "EUR")
 * @param locale - Locale for formatting (default: "en-US")
 * @returns Formatted money string (e.g., "$123.45")
 *
 * @example
 * formatMoney(12345, 'USD') // "$123.45"
 * formatMoney(10000, 'EUR', 'de-DE') // "100,00 €"
 * formatMoney(1000, 'JPY') // "¥1,000"
 */
export function formatMoney(amountMinorUnits, currencyCode = 'USD', locale = 'en-US') {
    const decimalPlaces = CurrencyUtils.getDecimalPlaces(currencyCode);
    // Convert minor units to major units
    // Backend stores amounts with 2 decimal places for all currencies
    // For currencies with 0 decimal places (JPY, KRW, etc.), divide by 100
    let value;
    if (decimalPlaces === 0) {
        // For zero-decimal currencies, backend still stores with 2 decimals
        value = amountMinorUnits / 100;
    }
    else {
        value = amountMinorUnits / Math.pow(10, decimalPlaces);
    }
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        }).format(value);
    }
    catch {
        // Fallback if Intl.NumberFormat fails
        const symbol = CurrencyUtils.getCurrencySymbol(currencyCode);
        return `${symbol}${value.toFixed(decimalPlaces)}`;
    }
}
export class CurrencyUtils {
    /**
     * Get currency from context or fallback to default
     */
    static getCurrency(context, defaultCurrency = 'USD') {
        let currencyCode;
        let isExplicit = false;
        // 1. URL ?currency= param takes highest priority (set by CRM preview, storefront links, or currency selector)
        const urlCurrency = typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('currency')
            : null;
        // 2. Persisted tgd_currency from storage/cookie (survives navigation between funnel steps)
        const storedCurrency = typeof window !== 'undefined'
            ? (localStorage.getItem('tgd_currency') || CurrencyUtils.getCookieValue('tgd_currency'))
            : null;
        if (urlCurrency) {
            currencyCode = urlCurrency.toUpperCase();
            isExplicit = true;
        }
        else if (storedCurrency) {
            currencyCode = storedCurrency.toUpperCase();
            isExplicit = true;
        }
        else if (typeof context?.currency === 'string') {
            currencyCode = context.currency;
        }
        else if (context?.currency?.code) {
            currencyCode = context.currency.code;
        }
        else if (context?.store?.presentmentCurrencies?.[0]) {
            currencyCode = context.store.presentmentCurrencies[0];
        }
        else {
            currencyCode = defaultCurrency;
        }
        // Validate against store's presentment currencies when available.
        // This catches both explicit overrides (URL/storage) and fallback values that
        // reference a currency the store doesn't support.
        const presentment = context?.store?.presentmentCurrencies;
        if (presentment?.length && !presentment.includes(currencyCode)) {
            console.warn(`[CurrencyUtils] Currency "${currencyCode}" is not in store presentmentCurrencies [${presentment.join(', ')}]. Falling back to ${presentment[0]}.`);
            currencyCode = presentment[0];
            // Update persisted storage so subsequent renders don't keep requesting the unsupported currency
            if (isExplicit && typeof window !== 'undefined') {
                try {
                    localStorage.setItem('tgd_currency', currencyCode);
                }
                catch { }
            }
        }
        return {
            code: currencyCode,
            symbol: this.getCurrencySymbol(currencyCode),
            name: this.getCurrencyName(currencyCode),
            decimalPlaces: this.getDecimalPlaces(currencyCode),
            isExplicit,
        };
    }
    static getCookieValue(name) {
        if (typeof document === 'undefined')
            return null;
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : null;
    }
    /**
     * Get currency symbol
     */
    static getCurrencySymbol(code) {
        const symbols = {
            USD: '$',
            EUR: '€',
            GBP: '£',
            JPY: '¥',
            CAD: 'C$',
            AUD: 'A$',
            CHF: 'CHF',
            CNY: '¥',
            SEK: 'kr',
            NOK: 'kr',
            DKK: 'kr',
            PLN: 'zł',
            CZK: 'Kč',
            HUF: 'Ft',
            RON: 'lei',
            BGN: 'лв',
            HRK: 'kn',
            RUB: '₽',
            UAH: '₴',
            TRY: '₺',
            BRL: 'R$',
            ARS: '$',
            CLP: '$',
            COP: '$',
            MXN: '$',
            PEN: 'S/',
            UYU: '$U',
            VEF: 'Bs',
            ZAR: 'R',
            EGP: '£',
            MAD: 'د.م.',
            TND: 'د.ت',
            DZD: 'د.ج',
            LYD: 'ل.د',
            NGN: '₦',
            GHS: '₵',
            KES: 'KSh',
            UGX: 'USh',
            TZS: 'TSh',
            ETB: 'Br',
            ZMW: 'ZK',
            BWP: 'P',
            SZL: 'L',
            LSL: 'L',
            NAD: 'N$',
            MUR: '₨',
            SCR: '₨',
            KWD: 'د.ك',
            BHD: 'د.ب',
            QAR: 'ر.ق',
            AED: 'د.إ',
            OMR: 'ر.ع.',
            YER: '﷼',
            SAR: 'ر.س',
            JOD: 'د.ا',
            LBP: 'ل.ل',
            ILS: '₪',
            INR: '₹',
            PKR: '₨',
            BDT: '৳',
            LKR: '₨',
            NPR: '₨',
            AFN: '؋',
            KZT: '₸',
            UZS: 'лв',
            KGS: 'лв',
            TJS: 'SM',
            TMT: 'T',
            AZN: '₼',
            GEL: '₾',
            AMD: '֏',
            KRW: '₩',
            THB: '฿',
            VND: '₫',
            IDR: 'Rp',
            MYR: 'RM',
            SGD: 'S$',
            PHP: '₱',
            TWD: 'NT$',
            HKD: 'HK$',
            MOP: 'MOP$',
            BND: 'B$',
            LAK: '₭',
            KHR: '៛',
            MMK: 'K',
            BOB: 'Bs',
            PYG: '₲',
            GTQ: 'Q',
            HNL: 'L',
            NIO: 'C$',
            CRC: '₡',
            PAB: 'B/.',
            DOP: 'RD$',
            JMD: 'J$',
            TTD: 'TT$',
            BBD: 'Bds$',
            XCD: 'EC$',
            AWG: 'ƒ',
            ANG: 'ƒ',
            SRD: '$',
            GYD: 'G$',
            VES: 'Bs.S',
            VED: 'Bs.D',
        };
        return symbols[code] || code;
    }
    /**
     * Get currency name
     */
    static getCurrencyName(code) {
        const names = {
            USD: 'US Dollar',
            EUR: 'Euro',
            GBP: 'British Pound',
            JPY: 'Japanese Yen',
            CAD: 'Canadian Dollar',
            AUD: 'Australian Dollar',
            CHF: 'Swiss Franc',
            CNY: 'Chinese Yuan',
            SEK: 'Swedish Krona',
            NOK: 'Norwegian Krone',
            DKK: 'Danish Krone',
            PLN: 'Polish Zloty',
            CZK: 'Czech Koruna',
            HUF: 'Hungarian Forint',
            RON: 'Romanian Leu',
            BGN: 'Bulgarian Lev',
            HRK: 'Croatian Kuna',
            RUB: 'Russian Ruble',
            UAH: 'Ukrainian Hryvnia',
            TRY: 'Turkish Lira',
            BRL: 'Brazilian Real',
            ARS: 'Argentine Peso',
            CLP: 'Chilean Peso',
            COP: 'Colombian Peso',
            MXN: 'Mexican Peso',
            PEN: 'Peruvian Sol',
            UYU: 'Uruguayan Peso',
            VEF: 'Venezuelan Bolivar',
            ZAR: 'South African Rand',
            EGP: 'Egyptian Pound',
            MAD: 'Moroccan Dirham',
            TND: 'Tunisian Dinar',
            DZD: 'Algerian Dinar',
            LYD: 'Libyan Dinar',
            NGN: 'Nigerian Naira',
            GHS: 'Ghanaian Cedi',
            KES: 'Kenyan Shilling',
            UGX: 'Ugandan Shilling',
            TZS: 'Tanzanian Shilling',
            ETB: 'Ethiopian Birr',
            ZMW: 'Zambian Kwacha',
            BWP: 'Botswana Pula',
            SZL: 'Swazi Lilangeni',
            LSL: 'Lesotho Loti',
            NAD: 'Namibian Dollar',
            MUR: 'Mauritian Rupee',
            SCR: 'Seychellois Rupee',
            KWD: 'Kuwaiti Dinar',
            BHD: 'Bahraini Dinar',
            QAR: 'Qatari Riyal',
            AED: 'UAE Dirham',
            OMR: 'Omani Rial',
            YER: 'Yemeni Rial',
            SAR: 'Saudi Riyal',
            JOD: 'Jordanian Dinar',
            LBP: 'Lebanese Pound',
            ILS: 'Israeli Shekel',
            INR: 'Indian Rupee',
            PKR: 'Pakistani Rupee',
            BDT: 'Bangladeshi Taka',
            LKR: 'Sri Lankan Rupee',
            NPR: 'Nepalese Rupee',
            AFN: 'Afghan Afghani',
            KZT: 'Kazakhstani Tenge',
            UZS: 'Uzbekistani Som',
            KGS: 'Kyrgyzstani Som',
            TJS: 'Tajikistani Somoni',
            TMT: 'Turkmenistani Manat',
            AZN: 'Azerbaijani Manat',
            GEL: 'Georgian Lari',
            AMD: 'Armenian Dram',
            KRW: 'South Korean Won',
            THB: 'Thai Baht',
            VND: 'Vietnamese Dong',
            IDR: 'Indonesian Rupiah',
            MYR: 'Malaysian Ringgit',
            SGD: 'Singapore Dollar',
            PHP: 'Philippine Peso',
            TWD: 'Taiwan Dollar',
            HKD: 'Hong Kong Dollar',
            MOP: 'Macanese Pataca',
            BND: 'Brunei Dollar',
            LAK: 'Lao Kip',
            KHR: 'Cambodian Riel',
            MMK: 'Myanmar Kyat',
            BOB: 'Bolivian Boliviano',
            PYG: 'Paraguayan Guarani',
            GTQ: 'Guatemalan Quetzal',
            HNL: 'Honduran Lempira',
            NIO: 'Nicaraguan Cordoba',
            CRC: 'Costa Rican Colon',
            PAB: 'Panamanian Balboa',
            DOP: 'Dominican Peso',
            JMD: 'Jamaican Dollar',
            TTD: 'Trinidad and Tobago Dollar',
            BBD: 'Barbadian Dollar',
            XCD: 'East Caribbean Dollar',
            AWG: 'Aruban Florin',
            ANG: 'Netherlands Antillean Guilder',
            SRD: 'Surinamese Dollar',
            GYD: 'Guyanese Dollar',
            VES: 'Venezuelan Bolivar Soberano',
            VED: 'Venezuelan Bolivar Digital',
        };
        return names[code] || code;
    }
    /**
     * Get decimal places for currency
     */
    static getDecimalPlaces(code) {
        const decimalPlaces = {
            JPY: 0,
            KRW: 0,
            VND: 0,
            IDR: 0,
            LAK: 0,
            KHR: 0,
            MMK: 0,
            PYG: 0,
            VEF: 0,
            VES: 0,
            VED: 0,
        };
        return decimalPlaces[code] ?? 2;
    }
}
